import type { TenantListQuery } from './api';

export type StatusFilter = 'all' | 'active' | 'suspended' | 'closed';
export type PaidFilter = 'any' | 'lapsed' | '7' | '14' | '30';
export type BillingFilter = 'all' | 'trial' | 'paid';

export const BILLING_OPTIONS: { value: BillingFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'trial', label: 'Trial' },
  { value: 'paid', label: 'Paid' },
];

export const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
];

export const PAID_OPTIONS: { value: PaidFilter; label: string }[] = [
  { value: 'any', label: 'Any time' },
  { value: 'lapsed', label: 'Lapsed (grace or locked)' },
  { value: '7', label: 'Ends within 7 days' },
  { value: '14', label: 'Ends within 14 days' },
  { value: '30', label: 'Ends within 30 days' },
];

const DAY_MS = 86_400_000;

function oneOf<T extends string>(value: string | null, options: { value: T }[], fallback: T): T {
  return options.find((o) => o.value === value)?.value ?? fallback;
}

/** Filters kept in the URL (UI_UX_GUIDELINES "Tables"), mapped to the API's query. */
export function buildTenantQuery(
  params: URLSearchParams,
  now: number,
): {
  query: TenantListQuery;
  status: StatusFilter;
  paid: PaidFilter;
  billing: BillingFilter;
  q: string;
} {
  const status = oneOf(params.get('status'), STATUS_OPTIONS, 'all');
  const paid = oneOf(params.get('paid'), PAID_OPTIONS, 'any');
  const billing = oneOf(params.get('billing'), BILLING_OPTIONS, 'all');
  const q = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const pageSize = Math.min(100, Math.max(1, Number(params.get('size')) || 25));
  const query: TenantListQuery = { page, pageSize };
  if (q.trim()) query.q = q.trim();
  if (status === 'active') {
    query.status = 'ACTIVE';
    query.suspended = 'false';
  } else if (status === 'suspended') query.suspended = 'true';
  else if (status === 'closed') query.status = 'CLOSED';
  if (paid !== 'any') {
    const days = paid === 'lapsed' ? 0 : Number(paid);
    query.paidThroughBefore = new Date(now + days * DAY_MS).toISOString();
  }
  if (billing !== 'all') query.billing = billing;
  return { query, status, paid, billing, q };
}
